import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../lib/toast.ts';
import { Layout } from '../components/Layout.tsx';
import { reportsAPI, territoriesAPI, regionAPI } from '../services/api.ts';
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import {
  CalendarDays,
  Download,
  Filter,
  Globe,
  MapPin,
  Send,
} from 'lucide-react';
import { PageHeader, StatusBadge } from '../components/app/index.ts';

const months = [
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

const years = Array.from({ length: 5 }, (_, i) =>
  (new Date().getFullYear() - i).toString(),
);

export const Reports: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Filters
  const [selectedMonth, setSelectedMonth] = useState(
    months[new Date().getMonth()].value,
  );
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  const [selectedTerritory, setSelectedTerritory] = useState<number | ''>('');
  const [selectedRegion, setSelectedRegion] = useState<number | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [isResending, setIsResending] = useState(false);

  // Data for filters
  const [territories, setTerritories] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);

  const fetchFiltersData = async () => {
    try {
      const response = await reportsAPI.getFilters();

      if (response.success && response.data) {
        setRegions(response.data.regions || []);
        setTerritories(response.data.territories || []);
      }
    } catch (err) {
      console.error('Error fetching filter data:', err);
    }
  };

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await reportsAPI.getAll({
        month: selectedMonth,
        year: selectedYear,
        territoryId: selectedTerritory || undefined,
        regionId: selectedRegion || undefined,
        status: selectedStatus || undefined,
      });

      if (response.success) {
        setReports(response.data);
      } else {
        setError(response.message || 'Failed to fetch reports');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching reports');
    } finally {
      setLoading(false);
    }
  }, [
    selectedMonth,
    selectedYear,
    selectedTerritory,
    selectedRegion,
    selectedStatus,
  ]);

  useEffect(() => {
    fetchFiltersData();
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Reset territory when region changes
  useEffect(() => {
    setSelectedTerritory('');
  }, [selectedRegion]);

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkResend = async () => {
    if (selectedReportIds.length === 0) return;

    // Filter only approved reports (UI should already handle this, but for safety)
    const approvedReports = reports.filter(
      (r) => selectedReportIds.includes(r._id) && r.status === 'approved',
    );

    if (approvedReports.length === 0) {
      toast.error('Only approved reports can be resent');
      return;
    }

    try {
      setIsResending(true);
      const res = await reportsAPI.bulkResendEmails(
        approvedReports.map((r) => r._id),
      );
      if (res.success) {
        setSelectedReportIds([]);
      }
    } catch (error) {
      console.error('Bulk resend error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedReportIds.length === reports.length) {
      setSelectedReportIds([]);
    } else {
      setSelectedReportIds(reports.map((r) => r._id));
    }
  };

  const toggleSelectReport = (id: string) => {
    setSelectedReportIds((prev) =>
      prev.includes(id) ? prev.filter((rid) => rid !== id) : [...prev, id],
    );
  };

  const selectedApprovedReportsCount = reports.filter(
    (r) => selectedReportIds.includes(r._id) && r.status === 'approved',
  ).length;

  const territoryBadges = (list: any[]) =>
    list.map((t: any) => (
      <Badge key={t.id} size="xs" variant="light" color="gray">
        {t.name}
      </Badge>
    ));

  return (
    <Layout>
      <PageHeader
        title="Expense Reports"
        description="A list of all expense reports for your company with filtering options."
        actions={
          selectedReportIds.length > 0 ? (
            <>
              <Text size="sm" c="dimmed">
                {selectedReportIds.length} selected (
                {selectedApprovedReportsCount} approved)
              </Text>
              <Button
                leftSection={<Send size={16} />}
                onClick={handleBulkResend}
                disabled={selectedApprovedReportsCount === 0 || isResending}
              >
                Resend Emails
              </Button>
            </>
          ) : undefined
        }
      />

      <Stack gap="lg">
        <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
          <Stack gap="md">
            <Group gap="xs">
              <Filter size={18} />
              <Title order={4}>Filters</Title>
            </Group>
            <Divider />
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
              <Select
                label="Month"
                leftSection={<CalendarDays size={16} />}
                data={months}
                value={selectedMonth}
                onChange={(value) => value && setSelectedMonth(value)}
                allowDeselect={false}
              />
              <Select
                label="Year"
                leftSection={<CalendarDays size={16} />}
                data={years}
                value={selectedYear}
                onChange={(value) => value && setSelectedYear(value)}
                allowDeselect={false}
              />
              <Select
                label="Region"
                placeholder="All Regions"
                leftSection={<Globe size={16} />}
                data={regions.map((r) => ({
                  value: String(r.id),
                  label: r.name,
                }))}
                value={selectedRegion === '' ? null : String(selectedRegion)}
                onChange={(value) =>
                  setSelectedRegion(value ? Number(value) : '')
                }
                clearable
              />
              <Select
                label="Territory"
                placeholder="All Territories"
                leftSection={<MapPin size={16} />}
                data={territories
                  .filter(
                    (t) => !selectedRegion || t.region_id === selectedRegion,
                  )
                  .map((t) => ({ value: String(t.id), label: t.name }))}
                value={
                  selectedTerritory === '' ? null : String(selectedTerritory)
                }
                onChange={(value) =>
                  setSelectedTerritory(value ? Number(value) : '')
                }
                clearable
              />
              <Select
                label="Status"
                placeholder="All Statuses"
                leftSection={<Filter size={16} />}
                data={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' },
                ]}
                value={selectedStatus || null}
                onChange={(value) => setSelectedStatus(value || '')}
                clearable
              />
            </SimpleGrid>
          </Stack>
        </Paper>

        <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : error ? (
            <Stack align="center" gap="xs" py="xl">
              <Text c="red">{error}</Text>
              <Button variant="subtle" onClick={fetchReports}>
                Try again
              </Button>
            </Stack>
          ) : reports.length === 0 ? (
            <Text ta="center" c="dimmed" py="xl">
              No reports found for the selected filters.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={1000}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>
                      <Checkbox
                        aria-label="Select all reports"
                        checked={
                          reports.length > 0 &&
                          selectedReportIds.length === reports.length
                        }
                        onChange={toggleSelectAll}
                      />
                    </Table.Th>
                    <Table.Th>Sender</Table.Th>
                    <Table.Th>Region</Table.Th>
                    <Table.Th>Territories</Table.Th>
                    <Table.Th>Project</Table.Th>
                    <Table.Th>Month/Year</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Created At</Table.Th>
                    <Table.Th ta="right">Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {reports.map((report) => (
                    <Table.Tr
                      key={report._id}
                      bg={
                        selectedReportIds.includes(report._id)
                          ? 'var(--mantine-primary-color-light)'
                          : undefined
                      }
                    >
                      <Table.Td>
                        <Checkbox
                          aria-label="Select report"
                          checked={selectedReportIds.includes(report._id)}
                          onChange={() => toggleSelectReport(report._id)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {report.sender_details?.name || 'N/A'}
                        </Text>
                        <Text size="xs" c="dimmed">
                          ID: {report.sender}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="blue" tt="none">
                          {report.sender_details?.region?.name || 'N/A'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} maw={200}>
                          {report.territory_details &&
                          report.territory_details.length > 0
                            ? territoryBadges(report.territory_details)
                            : report.sender_details?.territories &&
                                report.sender_details.territories.length > 0
                              ? territoryBadges(
                                  report.sender_details.territories,
                                )
                              : 'N/A'}
                        </Group>
                      </Table.Td>
                      <Table.Td>{report.project_details?.name || 'N/A'}</Table.Td>
                      <Table.Td>
                        {months.find((m) => m.value === report.selectedMonth)
                          ?.label || report.selectedMonth}{' '}
                        {report.selectedYear}
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge status={report.status} />
                      </Table.Td>
                      <Table.Td>
                        {new Date(report.createdAt).toLocaleDateString()}
                      </Table.Td>
                      <Table.Td ta="right">
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          leftSection={<Download size={14} />}
                          onClick={() =>
                            handleDownload(
                              report.reportURL,
                              `Report-${report.sender_details?.name}-${report.selectedMonth}.pdf`,
                            )
                          }
                        >
                          Download
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Paper>
      </Stack>
    </Layout>
  );
};
